import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router'
import { api } from '../api/client'
import type { Erfassung, Fach, Katalog, Schrank, Sonderfall, Versorgungspunkt } from '../api/typen'
import AppRahmen from '../komponenten/AppRahmen'
import { beschreibe, istZugeordnet } from '../komponenten/beschreibung'
import ErgebnisFormular, { ausErgebnis, type ErgebnisWerte } from '../komponenten/ErgebnisFormular'
import Konfidenz from '../komponenten/Konfidenz'
import ReportInhalt from '../komponenten/ReportInhalt'
import StuecklisteInhalt from '../komponenten/StuecklisteInhalt'

type Reiter = 'pruefung' | 'stueckliste' | 'report'

const REITER: { schluessel: Reiter; text: string }[] = [
  { schluessel: 'pruefung', text: 'Prüfung' },
  { schluessel: 'stueckliste', text: 'Stückliste' },
  { schluessel: 'report', text: 'Report' },
]

type Auswahl = { art: 'fach'; id: number } | { art: 'sonderfall'; id: number }

/** Ein Eintrag der linken Navigation mit allem, wonach gesucht werden kann. */
interface Eintrag {
  auswahl: Auswahl
  punkt: Versorgungspunkt
  schrank: Schrank | null
  fach: Fach | null
  sonderfall: Sonderfall | null
  offen: boolean
  suchtext: string
}

/**
 * Schritt 9: Pruefung und Nachbearbeitung im Buero. Links die Faecher und Sonderfaelle, nach
 * Versorgungspunkt gruppiert und durchsuchbar; in der Mitte die Bilder, rechts Vorschlag und
 * Ergebnis nebeneinander (P9-01). Jede Entscheidung bleibt nachtraeglich aenderbar, Vorschlag und
 * Korrektur bleiben unterscheidbar (P9-02). Stueckliste (P10-01) und Verifizierungsreport (P10-03)
 * stehen als eigene Reiter daneben, damit die Pruefung am Schreibtisch an einem Ort bleibt.
 */
export default function Buero() {
  const { erfassungId } = useParams()
  const navigiere = useNavigate()
  const abfragen = useQueryClient()

  const [reiter, setReiter] = useState<Reiter>('pruefung')
  const [gewaehlt, setGewaehlt] = useState<Auswahl | null>(null)
  const [nurOffene, setNurOffene] = useState(false)
  const [suche, setSuche] = useState('')
  // Die Formularwerte tragen den Eintrag mit, zu dem sie gehoeren. Wechselt der angezeigte Eintrag
  // - durch Antippen oder weil die Suche ihn ausblendet -, duerfen Aenderungen am vorigen Fach nicht
  // am neuen erscheinen.
  const [formular, setFormular] = useState<{
    schluessel: string
    werte: ErgebnisWerte
    beruehrt: boolean
  } | null>(null)

  const erfassung = useQuery({
    queryKey: ['erfassung', erfassungId],
    queryFn: () => api.get<Erfassung>(`/api/erfassungen/${erfassungId}`),
  })
  const katalog = useQuery({ queryKey: ['katalog'], queryFn: () => api.get<Katalog>('/api/katalog') })

  const alle = useMemo(() => eintraege(erfassung.data), [erfassung.data])

  // Die Suche laeuft ausschliesslich hier im Browser: die Daten der Erfassung sind ohnehin geladen.
  const sichtbar = useMemo(() => {
    const begriff = suche.trim().toLowerCase()
    return alle.filter(
      (eintrag) => (!nurOffene || eintrag.offen) && (begriff === '' || eintrag.suchtext.includes(begriff)),
    )
  }, [alle, suche, nurOffene])

  const aktuell =
    sichtbar.find(
      (eintrag) => gewaehlt && eintrag.auswahl.art === gewaehlt.art && eintrag.auswahl.id === gewaehlt.id,
    ) ?? sichtbar[0]

  const aktuellesFach = aktuell?.fach ?? null
  const schluessel = aktuell ? `${aktuell.auswahl.art}-${aktuell.auswahl.id}` : null

  // Nachbelegen bei jedem neuen Serverstand, solange am Formular dieses Eintrags nichts angetippt
  // wurde - sonst bliebe ein zwischengespeicherter Stand stehen.
  useEffect(() => {
    if (!aktuellesFach || !schluessel) return
    setFormular((bisher) =>
      bisher && bisher.schluessel === schluessel && bisher.beruehrt
        ? bisher
        : {
            schluessel,
            werte: ausErgebnis(aktuellesFach.nutzerErgebnis ?? aktuellesFach.kiVorschlag),
            beruehrt: false,
          },
    )
  }, [aktuellesFach, schluessel])

  const werte = formular && formular.schluessel === schluessel ? formular.werte : null

  const speichern = useMutation({
    mutationFn: () => api.post<Fach>(`/api/faecher/${aktuellesFach!.id}/bestaetigen`, werte),
    onSuccess: (gespeichert) => {
      abfragen.setQueryData(['fach', String(gespeichert.id)], gespeichert)
      abfragen.invalidateQueries({ queryKey: ['erfassung', erfassungId] })
      abfragen.invalidateQueries({ queryKey: ['stueckliste', erfassungId] })
      // Gespeichert ist gespeichert: der naechste Serverstand darf das Formular wieder belegen.
      setFormular((bisher) => (bisher ? { ...bisher, beruehrt: false } : bisher))
    },
  })

  const fuss =
    reiter === 'stueckliste' ? (
      <button
        className="btn btn-primaer"
        onClick={() => window.open(`/api/erfassungen/${erfassungId}/export`, '_blank')}
      >
        JSON-Export herunterladen
      </button>
    ) : reiter === 'report' ? (
      <button className="btn btn-primaer nicht-drucken" onClick={() => window.print()}>
        Drucken oder als PDF sichern
      </button>
    ) : aktuell?.sonderfall ? (
      <button
        className="btn btn-primaer"
        onClick={() => navigiere(`/sonderfaelle/${aktuell.sonderfall!.id}`)}
      >
        Sonderfall bearbeiten
      </button>
    ) : (
      <button
        className="btn btn-primaer"
        disabled={!aktuellesFach || !werte || speichern.isPending}
        onClick={() => speichern.mutate()}
      >
        {speichern.isPending ? 'Speichert …' : 'Ergebnis übernehmen'}
      </button>
    )

  return (
    <AppRahmen
      titel="Prüfung im Büro"
      unter={erfassung.data?.standort}
      zurueck={`/erfassungen/${erfassungId}`}
      pfad={
        erfassung.data
          ? {
              erfassungId: erfassung.data.id,
              standort: erfassung.data.standort,
              versorgungspunktId: null,
              versorgungspunkt: null,
              schrankId: null,
              schrank: null,
            }
          : undefined
      }
      ebene="erfassung"
      pfadZusatz="Prüfung im Büro"
      breit
      fuss={fuss}
    >
      <div className="tabs nicht-drucken" role="tablist" aria-label="Bereich">
        {REITER.map((eintrag) => (
          <button
            key={eintrag.schluessel}
            className="tab"
            role="tab"
            aria-selected={reiter === eintrag.schluessel}
            onClick={() => setReiter(eintrag.schluessel)}
          >
            {eintrag.text}
          </button>
        ))}
      </div>

      {erfassung.isPending && <div className="spinner" />}

      {reiter === 'stueckliste' && <StuecklisteInhalt erfassungId={erfassungId!} />}

      {reiter === 'report' && erfassung.data && <ReportInhalt erfassung={erfassung.data} />}

      {reiter === 'pruefung' && erfassung.data && (
        <div className="dk-koerper">
          <nav className="dk-nav" aria-label="Fächer und Sonderfälle">
            <input
              className="dk-suche"
              type="search"
              placeholder="Suchen: Fach, Schrank, Versorgungspunkt"
              value={suche}
              onChange={(ereignis) => setSuche(ereignis.target.value)}
              aria-label="Einträge filtern"
            />
            <button
              className="opt klein"
              aria-pressed={nurOffene}
              onClick={() => setNurOffene((wert) => !wert)}
            >
              {nurOffene ? 'Alle zeigen' : 'Nur offene zeigen'}
            </button>

            {sichtbar.length === 0 && <p className="sek">Kein Eintrag passt zur Suche.</p>}

            {gruppiert(sichtbar).map((gruppe) => (
              <div key={gruppe.punkt.id} className="dk-gruppe">
                <span className="dk-gruppenkopf">
                  {gruppe.punkt.bezeichnung} <span className="sek">{gruppe.punkt.vpCode}</span>
                </span>
                {gruppe.abschnitte.map((abschnitt) => (
                  <div key={abschnitt.schluessel} className="dk-abschnitt">
                    <span className="dk-abschnittskopf">{abschnitt.titel}</span>
                    {abschnitt.eintraege.map((eintrag) => (
                      <button
                        key={`${eintrag.auswahl.art}-${eintrag.auswahl.id}`}
                        aria-current={
                          aktuell?.auswahl.art === eintrag.auswahl.art &&
                          aktuell.auswahl.id === eintrag.auswahl.id
                        }
                        onClick={() => setGewaehlt(eintrag.auswahl)}
                      >
                        {eintrag.fach
                          ? eintrag.fach.fachCode +
                            (eintrag.fach.bezeichnung ? ` · ${eintrag.fach.bezeichnung}` : '')
                          : eintrag.sonderfall!.bezeichnung}
                        {eintrag.offen ? '' : ' ✓'}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </nav>

          <div className="dk-bilder">
            {aktuell?.fach && (
              <>
                <span className="sek">
                  {aktuell.punkt.bezeichnung} · {aktuell.schrank?.bezeichnung}
                </span>
                {aktuell.fach.bildObenVorhanden && (
                  <img src={`/api/faecher/${aktuell.fach.id}/bild/oben`} alt="Draufsicht" />
                )}
                {aktuell.fach.bildVorneVorhanden && (
                  <img src={`/api/faecher/${aktuell.fach.id}/bild/vorne`} alt="Frontalsicht" />
                )}
              </>
            )}
            {aktuell?.sonderfall && (
              <SonderfallBilder sonderfall={aktuell.sonderfall} punktName={aktuell.punkt.bezeichnung} />
            )}
          </div>

          <div className="dk-detail">
            {aktuell?.fach && (
              <>
                {aktuell.fach.kiVorschlag && (
                  <div className="gruppe">
                    <div className="kopfzeile">
                      <span className="label">Vorschlag des Systems</span>
                      <Konfidenz wert={aktuell.fach.kiVorschlag.konfidenz} />
                    </div>
                    <span className="sek">{beschreibe(aktuell.fach.kiVorschlag)}</span>
                    {aktuell.fach.kiVorschlag.hinweise.map((hinweis) => (
                      <p key={hinweis} className="hinweiszeile">
                        {hinweis}
                      </p>
                    ))}
                  </div>
                )}

                {werte && katalog.data && (
                  <ErgebnisFormular
                    werte={werte}
                    aendern={(neu) => setFormular({ schluessel: schluessel!, werte: neu, beruehrt: true })}
                    katalog={katalog.data}
                    vorschlag={aktuell.fach.kiVorschlag ? ausErgebnis(aktuell.fach.kiVorschlag) : null}
                  />
                )}

                {aktuell.fach.nutzerErgebnis && (
                  <p className="sek">
                    Zuletzt bestätigt von {aktuell.fach.nutzerErgebnis.bestaetigtVon} am{' '}
                    {new Date(aktuell.fach.nutzerErgebnis.bestaetigtAm ?? '').toLocaleString('de-DE')}
                  </p>
                )}
                {speichern.isError && <p className="hinweiszeile">{(speichern.error as Error).message}</p>}
              </>
            )}

            {aktuell?.sonderfall && <SonderfallZuordnung sonderfall={aktuell.sonderfall} />}
          </div>
        </div>
      )}
    </AppRahmen>
  )
}

/** Gesamtfoto und Ebenenfotos eines Sonderfalls in der Reihenfolge der Aufnahme (P7-02). */
function SonderfallBilder({ sonderfall, punktName }: { sonderfall: Sonderfall; punktName: string }) {
  return (
    <>
      <span className="sek">
        {punktName} · Sonderfall {sonderfall.sonderfallCode}
      </span>
      <span className="label">1 · Gesamtfoto</span>
      {sonderfall.gesamtfotoVorhanden ? (
        <img src={`/api/sonderfaelle/${sonderfall.id}/gesamtfoto`} alt="Gesamtfoto" />
      ) : (
        <p className="hinweiszeile">Noch kein Gesamtfoto.</p>
      )}
      <span className="label">2 · Ebenen, von oben nach unten</span>
      {sonderfall.ebenen.map((ebene) =>
        ebene.fotoVorhanden ? (
          <figure key={ebene.ebeneNr} style={{ margin: 0 }}>
            <img
              src={`/api/sonderfaelle/${sonderfall.id}/ebenen/${ebene.ebeneNr}/foto`}
              alt={`Ebene ${ebene.ebeneNr}`}
            />
            <figcaption className="sek">Ebene {ebene.ebeneNr}</figcaption>
          </figure>
        ) : (
          <p key={ebene.ebeneNr} className="hinweiszeile">
            Ebene {ebene.ebeneNr}: Foto fehlt
          </p>
        ),
      )}
    </>
  )
}

/** Die manuelle Zuordnung je Ebene (P7-03); geaendert wird sie in der Sonderfall-Ansicht. */
function SonderfallZuordnung({ sonderfall }: { sonderfall: Sonderfall }) {
  return (
    <div className="gruppe">
      <span className="label">{sonderfall.bezeichnung}</span>
      {sonderfall.beschreibungFreitext && <p className="notiz">{sonderfall.beschreibungFreitext}</p>}
      <span className="sek">
        Keine Bilderkennung - die Werte wurden vor Ort von Hand zugeordnet. Ändern lassen sie sich in der
        Sonderfall-Ansicht.
      </span>
      {sonderfall.ebenen.map((ebene) => (
        <div key={ebene.ebeneNr} className="karte">
          <div className="zeile">
            <b>Ebene {ebene.ebeneNr}</b>
            <span className={istZugeordnet(ebene) ? 'st st-ok' : 'st st-warn'}>
              {istZugeordnet(ebene) ? 'zugeordnet' : 'offen'}
            </span>
          </div>
          <span className="sek">{istZugeordnet(ebene) ? beschreibe(ebene) : 'noch nicht zugeordnet'}</span>
        </div>
      ))}
    </div>
  )
}

/** Flacht die Erfassung in Navigationseintraege ab, samt Suchtext aus allen uebergeordneten Namen. */
function eintraege(erfassung: Erfassung | undefined): Eintrag[] {
  if (!erfassung) return []
  const liste: Eintrag[] = []
  for (const punkt of erfassung.versorgungspunkte) {
    for (const schrank of punkt.schraenke) {
      for (const fach of schrank.faecher) {
        liste.push({
          auswahl: { art: 'fach', id: fach.id },
          punkt,
          schrank,
          fach,
          sonderfall: null,
          offen: fach.status !== 'bestaetigt',
          suchtext: [
            punkt.bezeichnung,
            punkt.vpCode,
            schrank.bezeichnung,
            schrank.schrankCode,
            fach.fachCode,
            fach.bezeichnung ?? '',
          ]
            .join(' ')
            .toLowerCase(),
        })
      }
    }
    for (const sonderfall of punkt.sonderfaelle) {
      const vollstaendig =
        sonderfall.gesamtfotoVorhanden &&
        sonderfall.ebenen.every((ebene) => ebene.fotoVorhanden && istZugeordnet(ebene))
      liste.push({
        auswahl: { art: 'sonderfall', id: sonderfall.id },
        punkt,
        schrank: null,
        fach: null,
        sonderfall,
        offen: !vollstaendig,
        suchtext: [
          punkt.bezeichnung,
          punkt.vpCode,
          'sonderfall',
          sonderfall.bezeichnung,
          sonderfall.sonderfallCode,
        ]
          .join(' ')
          .toLowerCase(),
      })
    }
  }
  return liste
}

/** Gruppiert die sichtbaren Eintraege nach Versorgungspunkt, darunter je Schrank bzw. Sonderfaelle. */
function gruppiert(sichtbar: Eintrag[]) {
  const gruppen: {
    punkt: Versorgungspunkt
    abschnitte: { schluessel: string; titel: string; eintraege: Eintrag[] }[]
  }[] = []

  for (const eintrag of sichtbar) {
    let gruppe = gruppen.find((vorhanden) => vorhanden.punkt.id === eintrag.punkt.id)
    if (!gruppe) {
      gruppe = { punkt: eintrag.punkt, abschnitte: [] }
      gruppen.push(gruppe)
    }
    const schluessel = eintrag.schrank ? `s-${eintrag.schrank.id}` : 'sonderfaelle'
    const titel = eintrag.schrank ? eintrag.schrank.bezeichnung : 'Sonderfälle'
    let abschnitt = gruppe.abschnitte.find((vorhanden) => vorhanden.schluessel === schluessel)
    if (!abschnitt) {
      abschnitt = { schluessel, titel, eintraege: [] }
      gruppe.abschnitte.push(abschnitt)
    }
    abschnitt.eintraege.push(eintrag)
  }
  return gruppen
}
